import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import './versionUpdateBanner.css';

const INDEX_URL = `${process.env.PUBLIC_URL || ''}/index.html`;
const POLL_INTERVAL_MS = 30000;

/**
 * Fingerprint the current deploy from hashed script/link assets in index.html.
 * Any new build uploaded to staging changes those hashes → clients get the banner.
 */
async function fetchDeployFingerprint() {
  const response = await fetch(`${INDEX_URL}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`Version check failed: ${response.status}`);
  }

  const html = await response.text();
  const assets = html.match(/\/static\/(?:js|css)\/[^"'?\s]+\.(?:js|css)/g) || [];
  const fingerprint = assets
    .map((asset) => asset.split('?')[0])
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort()
    .join('|');

  return fingerprint || null;
}

class VersionUpdateBanner extends Component {
  state = {
    updateAvailable: false,
  };

  clientFingerprint = null;
  pollTimer = null;

  componentDidMount() {
    // Dev server assets never change the way production hashed builds do.
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    this.initVersionCheck();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  componentWillUnmount() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  initVersionCheck = async () => {
    try {
      this.clientFingerprint = await fetchDeployFingerprint();
      if (this.clientFingerprint) {
        this.startPolling();
      }
    } catch (error) {
      console.warn('Version update check could not start:', error.message);
    }
  };

  startPolling = () => {
    this.stopPolling();
    this.pollTimer = setInterval(this.checkForUpdate, POLL_INTERVAL_MS);
  };

  stopPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.pollTimer = null;
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.checkForUpdate();
      if (this.clientFingerprint && !this.state.updateAvailable) {
        this.startPolling();
      }
    } else {
      this.stopPolling();
    }
  };

  checkForUpdate = async () => {
    if (!this.clientFingerprint || this.state.updateAvailable) {
      return;
    }

    try {
      const latestFingerprint = await fetchDeployFingerprint();
      if (latestFingerprint && latestFingerprint !== this.clientFingerprint) {
        this.setState({ updateAvailable: true });
        this.stopPolling();
      }
    } catch (error) {
      // Ignore transient network errors during polling.
    }
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    const { updateAvailable } = this.state;
    if (!updateAvailable) {
      return null;
    }

    return (
      <div className="version-update-banner" role="status" aria-live="polite">
        <div className="version-update-banner__content">
          <div className="version-update-banner__info">
            <span className="version-update-banner__icon" aria-hidden="true">
              <FontAwesomeIcon icon={faBullhorn} />
            </span>
            <div className="version-update-banner__text">
              <span className="version-update-banner__title">
                A new version of the app is available!
              </span>
              <span className="version-update-banner__message">
                Please refresh to get the latest updates and improvements.
              </span>
            </div>
          </div>
          <div className="version-update-banner__actions">
            <button
              type="button"
              className="version-update-banner__btn version-update-banner__btn--primary"
              onClick={this.handleRefresh}
            >
              <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
              Refresh now
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default VersionUpdateBanner;
